import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorRequest - The automatic gift request error element.
 * @property {HTMLElement} [cartDiscountErrorNetwork] - Network failure while applying a regular code.
 */

/**
 * Erreur de requête panier qui garde l'étape et le statut HTTP pour le funnel ERP.
 * @param {string} stage
 * @param {number} status
 */
function cartRequestError(stage, status) {
  const error = new Error(`Cart request failed (${stage} ${status})`);
  error.name = 'CartApiError';
  Object.assign(error, { stage, status });
  return error;
}

/**
 * Relance une fois une coupure réseau passagère (TypeError de fetch). Jamais une annulation :
 * AbortError signifie qu'une saisie plus récente a pris le relais. Appliquer ou retirer un code
 * est idempotent — la liste complète des codes est renvoyée à chaque fois.
 * @param {() => Promise<Response>} call
 */
async function fetchWithNetworkRetry(call) {
  try {
    return await call();
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    await new Promise((resolve) => setTimeout(resolve, 800));
    return call();
  }
}

/**
 * Détail d'échec envoyé au funnel : étape, type d'erreur, code HTTP et durée. Aucun contenu client.
 * @param {any} error
 * @param {string} fallbackStage
 * @param {number} startedAt
 */
function promoErrorDetails(error, fallbackStage, startedAt) {
  /** @type {Record<string, string | number>} */
  const details = {
    error_stage: error?.stage || fallbackStage,
    error_name: String(error?.name || 'Error').replace(/[^A-Za-z]/g, '').slice(0, 40) || 'Error',
    duration_ms: Math.max(0, Math.round(Date.now() - startedAt)),
  };
  if (typeof error?.status === 'number') details.http_status = error.status;
  return details;
}

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  // Shared by the desktop and mobile forms to prevent duplicate gift requests.
  static #petalsPromoPending = false;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    if (CartDiscount.#petalsPromoPending) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    const isPetalsPromo = discountCodeValue.toUpperCase() === 'WTHGVNNY3GRS';
    if (isPetalsPromo) CartDiscount.#petalsPromoPending = true;

    const abortController = this.#createAbortController();
    const startedAt = Date.now();
    let stage = 'get_cart';

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (!isPetalsPromo && existingDiscounts.includes(discountCodeValue)) return;
      window.SendloveCartAnalytics?.track('cart_promo_submit', { action: 'apply', location: this.dataset.instance === '-mobile' ? 'gift_recap' : 'summary' });

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      this.refs.cartDiscountErrorRequest?.classList.add('hidden');
      this.refs.cartDiscountErrorNetwork?.classList.add('hidden');

      const updates = {};
      if (isPetalsPromo) {
        const cartResponse = await fetchWithNetworkRetry(() => fetch(`${Theme.routes.cart_url}.js`, {
          signal: abortController.signal,
          cache: 'no-store',
        }));
        if (!cartResponse.ok) throw cartRequestError('get_cart', cartResponse.status);
        const cart = await cartResponse.json();
        if (!cart.items.some((item) => item.handle === 'petales-de-roses')) {
          // Classique / Beaucoup, shared with the composer's ADDONS_CONFIG.
          updates['63857386848633'] = 1;
        }
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...new Set([...existingDiscounts, isPetalsPromo ? discountCodeValue.toUpperCase() : discountCodeValue])].join(','),
          ...(isPetalsPromo ? { updates } : {}),
          sections: [this.dataset.sectionId],
        }),
      });

      stage = 'cart_update';
      const response = await fetchWithNetworkRetry(() => fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      }));

      if (!response.ok) throw cartRequestError('cart_update', response.status);
      const data = await response.json();

      if (
        (isPetalsPromo && !data.discount_codes.some((discount) =>
          discount.code.toUpperCase() === discountCodeValue.toUpperCase() && discount.applicable === true
        )) ||
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toUpperCase() === discountCodeValue.toUpperCase() && discount.applicable === false;
        })
      ) {
        if (updates['63857386848633']) {
          // A refused coupon must not leave automatically added, paid petals.
          stage = 'rollback';
          const rollbackResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
            body: JSON.stringify({
              updates: { '63857386848633': 0 },
              discount: existingDiscounts.join(','),
              sections: [this.dataset.sectionId],
            }),
          }));
          if (!rollbackResponse.ok) throw cartRequestError('rollback', rollbackResponse.status);
          const rollback = await rollbackResponse.json();
          document.dispatchEvent(new DiscountUpdateEvent(rollback, this.id));
          morphSection(this.dataset.sectionId, rollback.sections[this.dataset.sectionId]);
        }
        discountCode.value = '';
        this.#handleDiscountError('discount_code', discountCodeValue, data);
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountInstance = Array.from(section?.querySelectorAll('cart-discount-component') || [])
        .find((element) => element.dataset.instance === this.dataset.instance);
      const discountCodes = discountInstance?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          !isPetalsPromo &&
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code.toUpperCase() === discountCodeValue.toUpperCase() && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'apply', outcome: 'applied' });
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      if (error?.name === 'AbortError') window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'apply', outcome: 'cancelled' });
      else window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'apply', outcome: 'request_error', ...promoErrorDetails(error, stage, startedAt) });
      if (error?.name !== 'AbortError') {
        // Un code normal échouait sans aucun message : le client ne savait pas qu'il devait réessayer.
        this.refs.cartDiscountError.classList.remove('hidden');
        (isPetalsPromo ? this.refs.cartDiscountErrorRequest : this.refs.cartDiscountErrorNetwork)?.classList.remove('hidden');
      }
    } finally {
      if (isPetalsPromo) CartDiscount.#petalsPromoPending = false;
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (CartDiscount.#petalsPromoPending) return;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);
    window.SendloveCartAnalytics?.track('cart_promo_remove');

    const abortController = this.#createAbortController();
    const startedAt = Date.now();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetchWithNetworkRetry(() => fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      }));

      if (!response.ok) throw cartRequestError('cart_update', response.status);
      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'remove', outcome: 'removed' });
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      if (error?.name === 'AbortError') window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'remove', outcome: 'cancelled' });
      else window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'remove', outcome: 'request_error', ...promoErrorDetails(error, 'cart_update', startedAt) });
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   * @param {string} [submittedCode] - Refused code, collected only by the ERP.
   * @param {object} [cart] - Cart response at the time of rejection.
   */
  #handleDiscountError(type, submittedCode, cart) {
    if (type === 'discount_code') {
      const code = (submittedCode || '').normalize('NFKC').trim().toUpperCase();
      if (/^[\p{L}\p{N}_-]{1,64}$/u.test(code)) {
        const params = { promo_code: code };
        // Capture only numeric cart data; personalisation, names and addresses stay out.
        if (cart && Array.isArray(cart.items) && cart.items.length <= 40) {
          const snapshot = {
            currency: cart.currency,
            subtotal: cart.original_total_price,
            items: cart.items.map(item => ({
              productId: String(item.product_id), variantId: String(item.variant_id),
              quantity: item.quantity, subtotal: item.original_line_price,
            })),
          };
          const encoded = JSON.stringify(snapshot);
          if (encoded.length <= 8000) params.promo_cart = encoded;
        }
        window.SendloveFunnelQueue?.push({ name: 'cart_promo_rejected', params });
      }
    }
    window.SendloveCartAnalytics?.track('cart_promo_result', { action: 'apply', outcome: type === 'discount_code' ? 'invalid_code' : 'shipping_deferred' });
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
